import {useCallback, useEffect} from "react";
import {css, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {WideContent} from "@pg-design/grid-module";
import {borderRadius, p, pl} from "@pg-design/helpers-css";
import {calculateRemSize, mb} from "@pg-design/helpers-css-strings";
import {Picture} from "@pg-design/picture-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {useFetch} from "@pg-mono/hooks";
import {head} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationSource} from "../../../application/utils/ApplicationSource";
import {GalleryCount} from "../../../atoms/GalleryCount";
import {GalleryVideoIcon} from "../../../atoms/GalleryVideoIcon";
import {getPropertyPlanAltText} from "../../../comparison_tool/utils/get_property_plan_modal_title";
import {propertyPlan2dTracking} from "../../../property/tracking/property_plans2d_tracking";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {useGetReelsListQuery} from "../../../reels/api/get_reels_list";
import {fetchOfferConstructionGalleryPictures, fetchOfferGalleryPictures, fetchPropertyGalleryPictures} from "../../actions/fetch_gallery_pictures";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IConstructionGalleryResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {GalleryHolderModal} from "./GalleryHolderModal";
import {GalleryHolderReel} from "./GalleryHolderReel";
import {GalleryHolderRightSection} from "./GalleryHolderRightSection";

const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");
const presaleOfferDefaultImg = require("../../images/presale_offer_default_img.jpg");

interface IProps {
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    offerBuildings?: IOfferBuilding[];
    property?: IPropertyDetail;
    onPlanImageOpen?: () => void;
    onModalClose?: () => void;
    isOpen: boolean;
}

export const GalleryHolder = (props: IProps) => {
    const {offer, property, handleGalleryModalChange} = props;
    const externalPlanUrl = property?.external_plan_url;
    const altText = getPropertyPlanAltText(offer.name, property);
    const {fetchStart: fetchOfferGalleryPicture, response: galleryPictures, fetchState} = useFetch(() => fetchOfferGalleryPictures(offer.id));
    const fetchPropertyGalleryPicturesCallback = useCallback(() => {
        if (property?.id) {
            return fetchPropertyGalleryPictures(property.id);
        }
        return Promise.resolve(null);
    }, [property?.id]);

    const {fetchStart: fetchPropertyGalleryPicture, response: propertyGalleryPictures} = useFetch(fetchPropertyGalleryPicturesCallback);

    const {fetchStart: fetchOfferConstructionGalleryPicturesStart, response: constructionPictures} = useFetch<IConstructionGalleryResponse>(() =>
        fetchOfferConstructionGalleryPictures(offer.id)
    );

    useEffect(() => {
        fetchOfferConstructionGalleryPicturesStart();
        fetchOfferGalleryPicture();
        if (property && property.id) {
            fetchPropertyGalleryPicture();
        }
    }, []);

    const planImage = head(property?.plan_image_pages || [])?.image.p_img_760x428;

    const onRightImageClick = () => {
        if (planImage) {
            propertyPlan2dTracking.gtm.openPropertyPlan();
        }
    };

    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const isPreSale = "pre_sale" in offer.configuration && !!offer.configuration.pre_sale;

    const offerGalleryLength = galleryPictures?.length || 0;
    const propertyGalleryLength = propertyGalleryPictures?.length || 0;

    const galleryLength = offerGalleryLength + propertyGalleryLength;

    const mainImgSources =
        isPreSale && !offer.main_image
            ? [
                  {minWidthPX: 0, src: presaleOfferDefaultImg375x212, height: 360, width: 448},
                  {minWidthPX: theme.breakpoints.numeric.sm, src: presaleOfferDefaultImg, height: 588, width: 1366}
              ]
            : [
                  {minWidthPX: 0, src: offer.main_image?.m_img_440x248, height: 360, width: 448},
                  {minWidthPX: theme.breakpoints.numeric.xs, src: offer.main_image?.m_img_760x428, height: 600, width: 448},
                  {minWidthPX: theme.breakpoints.numeric.sm, src: offer.main_image?.m_img_983x552, height: 588, width: 1366},
                  {minWidthPX: theme.breakpoints.numeric.md, src: offer.main_image?.m_img_760x428, height: 826, width: 1919}
              ];

    const externalPlanImage = property?.external_plan_url ? [{minWidthPX: 0, src: property.external_plan_url, height: 360, width: 480}] : null;
    const properyPlanImages = property?.plan_image_pages?.[0]?.image
        ? [
              {minWidthPX: 0, src: property?.plan_image_pages[0].image.p_img_375x250, height: 360, width: 448},
              {minWidthPX: theme.breakpoints.numeric.xs, src: property?.plan_image_pages[0].image.p_img_760x428, height: 600, width: 448},
              {minWidthPX: theme.breakpoints.numeric.sm, src: property?.plan_image_pages[0].image.p_img_980, height: 588, width: 1366}
          ]
        : null;
    const mainPlanImage = externalPlanImage || properyPlanImages || mainImgSources;

    const onLeftImageClick = () => {
        const initialTab = isMobile && (externalPlanImage || properyPlanImages) ? OfferModalActiveTab.PLAN : OfferModalActiveTab.GALLERY;
        handleGalleryModalChange(initialTab);
    };

    const {data: reelList} = useGetReelsListQuery({offerId: offer.id}, {skip: !!property?.id});
    const firstReel = reelList?.results?.[0] || null;
    const isReelAvailable = !!firstReel;

    return (
        <WideContent>
            <div css={[galleryHolderStyle, isReelAvailable && galleryHolderWithReelStyle]}>
                {isReelAvailable ? (
                    <div css={galleryReelSlot}>
                        <GalleryHolderReel reelData={firstReel} offer={props.offer} />
                    </div>
                ) : null}

                <div css={galleryLeftSideHolder} onClick={onLeftImageClick} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OPEN_GALLERY_BUTTON}>
                    <div>
                        <Picture sources={mainPlanImage} alt={offer.name} css={mainImageMobileStyle} fetchPriority="high" />
                        <Picture sources={mainImgSources} alt={offer.name} css={mainImageStyle} fetchPriority="high" />
                        <div css={imageCounterHolder}>
                            {offer.video && <GalleryVideoIcon />}
                            <GalleryCount photosAmount={galleryLength + 1} />
                        </div>
                    </div>
                </div>

                <GalleryRightSideHolder hasReelBeenShown={isReelAvailable} hasExternalPlan={Boolean(externalPlanUrl)} onClick={onRightImageClick}>
                    <GalleryHolderRightSection
                        offer={props.offer}
                        planImage={planImage}
                        handleGalleryModalChange={props.handleGalleryModalChange}
                        galleryPictures={galleryPictures}
                        galleryFetchState={fetchState}
                        propertyGalleryPictures={propertyGalleryPictures}
                        externalPlan={externalPlanUrl}
                        displayOnlyOneColumn={isReelAvailable}
                        planImageAltText={altText}
                    />
                </GalleryRightSideHolder>
            </div>

            <GalleryHolderModal
                {...props}
                galleryPictures={galleryPictures}
                propertyGalleryPictures={propertyGalleryPictures}
                constructionGalleryPictures={constructionPictures}
            />
        </WideContent>
    );
};

const galleryHolderStyle = css`
    ${borderRadius(3)};
    overflow: hidden;
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    grid-row-gap: ${calculateRemSize(2)};
    margin-bottom: 1.5rem;

    img {
        cursor: pointer;
        width: 100%;
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${mb(5)};
        grid-template-columns: 1fr 1fr;
        grid-column-gap: ${calculateRemSize(2)};
    }
`;

const galleryHolderWithReelStyle = css`
    ${borderRadius(0)};

    @media (min-width: ${getThemeBreakpoint().xs}) {
        ${borderRadius(2)};
        grid-template-columns: 1fr 3.17fr;
        grid-template-rows: 1fr;
        grid-column-gap: ${calculateRemSize(2)};
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        margin-bottom: 4rem;
        grid-template-columns: 1fr 3.17fr 1.52fr;
    }
`;

const galleryReelSlot = css`
    ${borderRadius(2)};
    position: relative;
    aspect-ratio: 259 / 462;
    overflow: hidden;

    @media (min-width: ${getThemeBreakpoint().xs}) {
        ${borderRadius(0)};
        aspect-ratio: auto;
        height: 100%;
    }
`;

const galleryLeftSideHolder = css`
    ${borderRadius(2)};
    position: relative;
    overflow: hidden;

    @media (min-width: ${getThemeBreakpoint().xs}) {
        ${borderRadius(0)};
        overflow: visible;
    }
`;

const GalleryRightSideHolder = styled.div<{hasExternalPlan: boolean; hasReelBeenShown: boolean}>`
    ${({hasReelBeenShown}) => pl(hasReelBeenShown ? 0 : 1)}
    display: none;

    @media (min-width: ${getThemeBreakpoint().md}) {
        display: initial;
    }

    ${({hasExternalPlan}) =>
        hasExternalPlan &&
        css`
            ${p(2)};
            background-color: #fff;
        `};
`;

const imageCounterHolder = css`
    position: absolute;
    bottom: 1.2rem;
    right: 1.2rem;
    display: flex;
    align-items: center;
    gap: 0.7rem;
`;

const mainImageStyle = css`
    display: none;

    @media (min-width: ${getThemeBreakpoint().md}) {
        display: block;
    }
`;

const mainImageMobileStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) {
        display: none;
    }
`;
import {useCallback, useEffect} from "react";
import {css, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {WideContent} from "@pg-design/grid-module";
import {borderRadius, p, pl} from "@pg-design/helpers-css";
import {calculateRemSize, mb} from "@pg-design/helpers-css-strings";
import {Picture} from "@pg-design/picture-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {useFetch} from "@pg-mono/hooks";
import {head} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationSource} from "../../../application/utils/ApplicationSource";
import {GalleryCount} from "../../../atoms/GalleryCount";
import {GalleryVideoIcon} from "../../../atoms/GalleryVideoIcon";
import {getPropertyPlanAltText} from "../../../comparison_tool/utils/get_property_plan_modal_title";
import {propertyPlan2dTracking} from "../../../property/tracking/property_plans2d_tracking";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {useGetReelsListQuery} from "../../../reels/api/get_reels_list";
import {fetchOfferConstructionGalleryPictures, fetchOfferGalleryPictures, fetchPropertyGalleryPictures} from "../../actions/fetch_gallery_pictures";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IConstructionGalleryResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {GalleryHolderModal} from "./GalleryHolderModal";
import {GalleryHolderReel} from "./GalleryHolderReel";
import {GalleryHolderRightSection} from "./GalleryHolderRightSection";

const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");
const presaleOfferDefaultImg = require("../../images/presale_offer_default_img.jpg");

interface IProps {
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    offerBuildings?: IOfferBuilding[];
    property?: IPropertyDetail;
    onPlanImageOpen?: () => void;
    onModalClose?: () => void;
    isOpen: boolean;
}

export const GalleryHolder = (props: IProps) => {
    const {offer, property, handleGalleryModalChange} = props;
    const externalPlanUrl = property?.external_plan_url;
    const altText = getPropertyPlanAltText(offer.name, property);
    const {fetchStart: fetchOfferGalleryPicture, response: galleryPictures, fetchState} = useFetch(() => fetchOfferGalleryPictures(offer.id));
    const fetchPropertyGalleryPicturesCallback = useCallback(() => {
        if (property?.id) {
            return fetchPropertyGalleryPictures(property.id);
        }
        return Promise.resolve(null);
    }, [property?.id]);

    const {fetchStart: fetchPropertyGalleryPicture, response: propertyGalleryPictures} = useFetch(fetchPropertyGalleryPicturesCallback);

    const {fetchStart: fetchOfferConstructionGalleryPicturesStart, response: constructionPictures} = useFetch<IConstructionGalleryResponse>(() =>
        fetchOfferConstructionGalleryPictures(offer.id)
    );

    useEffect(() => {
        fetchOfferConstructionGalleryPicturesStart();
        fetchOfferGalleryPicture();
        if (property && property.id) {
            fetchPropertyGalleryPicture();
        }
    }, []);

    const planImage = head(property?.plan_image_pages || [])?.image.p_img_760x428;

    const onRightImageClick = () => {
        if (planImage) {
            propertyPlan2dTracking.gtm.openPropertyPlan();
        }
    };

    const theme = useTheme();
    const {isMobile} = useUserDevice();

    const isPreSale = "pre_sale" in offer.configuration && !!offer.configuration.pre_sale;

    const offerGalleryLength = galleryPictures?.length || 0;
    const propertyGalleryLength = propertyGalleryPictures?.length || 0;

    const galleryLength = offerGalleryLength + propertyGalleryLength;

    const mainImgSources =
        isPreSale && !offer.main_image
            ? [
                  {minWidthPX: 0, src: presaleOfferDefaultImg375x212, height: 360, width: 448},
                  {minWidthPX: theme.breakpoints.numeric.sm, src: presaleOfferDefaultImg, height: 588, width: 1366}
              ]
            : [
                  {minWidthPX: 0, src: offer.main_image?.m_img_440x248, height: 360, width: 448},
                  {minWidthPX: theme.breakpoints.numeric.xs, src: offer.main_image?.m_img_760x428, height: 600, width: 448},
                  {minWidthPX: theme.breakpoints.numeric.sm, src: offer.main_image?.m_img_983x552, height: 588, width: 1366},
                  {minWidthPX: theme.breakpoints.numeric.md, src: offer.main_image?.m_img_760x428, height: 826, width: 1919}
              ];

    const externalPlanImage = property?.external_plan_url ? [{minWidthPX: 0, src: property.external_plan_url, height: 360, width: 480}] : null;
    const properyPlanImages = property?.plan_image_pages?.[0]?.image
        ? [
              {minWidthPX: 0, src: property?.plan_image_pages[0].image.p_img_375x250, height: 360, width: 448},
              {minWidthPX: theme.breakpoints.numeric.xs, src: property?.plan_image_pages[0].image.p_img_760x428, height: 600, width: 448},
              {minWidthPX: theme.breakpoints.numeric.sm, src: property?.plan_image_pages[0].image.p_img_980, height: 588, width: 1366}
          ]
        : null;
    const mainPlanImage = externalPlanImage || properyPlanImages || mainImgSources;

    const onLeftImageClick = () => {
        const initialTab = isMobile && (externalPlanImage || properyPlanImages) ? OfferModalActiveTab.PLAN : OfferModalActiveTab.GALLERY;
        handleGalleryModalChange(initialTab);
    };

    const {data: reelList} = useGetReelsListQuery({offerId: offer.id}, {skip: !!property?.id});
    const firstReel = reelList?.results?.[0] || null;
    const isReelAvailable = !!firstReel;

    return (
        <WideContent>
            <div css={[galleryHolderStyle, isReelAvailable && galleryHolderWithReelStyle]}>
                {isReelAvailable ? (
                    <div css={galleryReelSlot}>
                        <GalleryHolderReel reelData={firstReel} offer={props.offer} />
                    </div>
                ) : null}

                <div css={galleryLeftSideHolder} onClick={onLeftImageClick} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OPEN_GALLERY_BUTTON}>
                    <div>
                        <Picture sources={mainPlanImage} alt={offer.name} css={mainImageMobileStyle} fetchPriority="high" />
                        <Picture sources={mainImgSources} alt={offer.name} css={mainImageStyle} fetchPriority="high" />
                        <div css={imageCounterHolder}>
                            {offer.video && <GalleryVideoIcon />}
                            <GalleryCount photosAmount={galleryLength + 1} />
                        </div>
                    </div>
                </div>

                <GalleryRightSideHolder hasReelBeenShown={isReelAvailable} hasExternalPlan={Boolean(externalPlanUrl)} onClick={onRightImageClick}>
                    <GalleryHolderRightSection
                        offer={props.offer}
                        planImage={planImage}
                        handleGalleryModalChange={props.handleGalleryModalChange}
                        galleryPictures={galleryPictures}
                        galleryFetchState={fetchState}
                        propertyGalleryPictures={propertyGalleryPictures}
                        externalPlan={externalPlanUrl}
                        displayOnlyOneColumn={isReelAvailable}
                        planImageAltText={altText}
                    />
                </GalleryRightSideHolder>
            </div>

            <GalleryHolderModal
                {...props}
                galleryPictures={galleryPictures}
                propertyGalleryPictures={propertyGalleryPictures}
                constructionGalleryPictures={constructionPictures}
            />
        </WideContent>
    );
};

const galleryHolderStyle = css`
    ${borderRadius(3)};
    overflow: hidden;
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    grid-row-gap: ${calculateRemSize(2)};
    margin-bottom: 1.5rem;

    img {
        cursor: pointer;
        width: 100%;
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${mb(5)};
        grid-template-columns: 1fr 1fr;
        grid-column-gap: ${calculateRemSize(2)};
    }
`;

const galleryHolderWithReelStyle = css`
    ${borderRadius(0)};

    @media (min-width: ${getThemeBreakpoint().xs}) {
        ${borderRadius(2)};
        grid-template-columns: 1fr 3.17fr;
        grid-template-rows: 1fr;
        grid-column-gap: ${calculateRemSize(2)};
    }

    @media (min-width: ${getThemeBreakpoint().md}) {
        margin-bottom: 4rem;
        grid-template-columns: 1fr 3.17fr 1.52fr;
    }
`;

const galleryReelSlot = css`
    ${borderRadius(2)};
    position: relative;
    aspect-ratio: 259 / 462;
    overflow: hidden;

    @media (min-width: ${getThemeBreakpoint().xs}) {
        ${borderRadius(0)};
        aspect-ratio: auto;
        height: 100%;
    }
`;

const galleryLeftSideHolder = css`
    ${borderRadius(2)};
    position: relative;
    overflow: hidden;

    @media (min-width: ${getThemeBreakpoint().xs}) {
        ${borderRadius(0)};
        overflow: visible;
    }
`;

const GalleryRightSideHolder = styled.div<{hasExternalPlan: boolean; hasReelBeenShown: boolean}>`
    ${({hasReelBeenShown}) => pl(hasReelBeenShown ? 0 : 1)}
    display: none;

    @media (min-width: ${getThemeBreakpoint().md}) {
        display: initial;
    }

    ${({hasExternalPlan}) =>
        hasExternalPlan &&
        css`
            ${p(2)};
            background-color: #fff;
        `};
`;

const imageCounterHolder = css`
    position: absolute;
    bottom: 1.2rem;
    right: 1.2rem;
    display: flex;
    align-items: center;
    gap: 0.7rem;
`;

const mainImageStyle = css`
    display: none;

    @media (min-width: ${getThemeBreakpoint().md}) {
        display: block;
    }
`;

const mainImageMobileStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) {
        display: none;
    }
`;
